import React, { useCallback, useEffect, useState } from 'react';
import { connect } from "react-redux";
import { getTranslate } from "react-localize-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap";
import moment from "moment";
import axios from "axios";
import { actions } from "react-redux-form";
import Datetime from "react-datetime";
import validator from "validator";
import { getFileColumns } from "../../containers/AppPages/Files/FilesGrid";
import { getDocumentColumns } from "../../containers/AppPages/Documents/DocumentsGrid";
import { fileActions, documentActions, settingActions } from "../../redux/actions";
import {
  handleErrors,
  getDocumentsParams,
  getFilesParams,
  getMomentLocaleByLang,
} from "../../utils";
import { TooltipWithAutoHide } from "..";

import './QuickDownloadButton.scss';

const INTERVALTIME = 5000; // 5 secs

const QuickDownloadButton = ({
  view,
  files,
  documents,
  fileSearch,
  docSearch,
  translate,
  isExternalUser,
  externalCustomerId,
  dispatch,
  isSender,
  user,
  subUser,
  isSubUser,
}) => {
  const getStartDate = useCallback(() => (
      view === 'filesView'
          ? fileSearch.Start
          : view === 'documentsView'
              ? docSearch.Start
              : null
  ), [docSearch.Start, fileSearch.Start, view]);
  const getEndDate = useCallback(() => (
      view === 'filesView'
          ? fileSearch.End
          : view === 'documentsView'
              ? docSearch.End
              : null
  ), [docSearch.End, fileSearch.End, view]);
  const getIsLessThanAMonth = useCallback((start = getStartDate(), end = getEndDate()) => {
    const dateMonthAgo = new Date(end);

    if (!(start instanceof Date && end instanceof Date)) return false;

    dateMonthAgo.setMonth(dateMonthAgo.getMonth() - 1);

    return dateMonthAgo.getTime() <= start.getTime();
  }, [getEndDate, getStartDate]);

  const [showDialog, setShowDialog] = useState(false);
  const [showTooMuchDialog, setShowTooMuchDialog] = useState(false);

  const _fileType = localStorage.getItem("downloadFileType");
  const [fileType, setFileType] = useState(_fileType || "csv");
  const _includeChannelDetails = localStorage.getItem(
    "downloadIncludeChannelDetails"
  );
  const [includeChannelDetails, setIncludeChannelDetails] = useState(
    !!_includeChannelDetails
  );
  const [showInvalidEmail, setShowInvalidEmail] = useState(false);
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());

  var currentUser = user;
  if (isSubUser) {
    currentUser = subUser;
  }

  const initEmail = currentUser?.email || "";
  const [email, setEmail] = useState(initEmail);

  const handleClick = async (event) => {
    event.preventDefault();
    if (view === "filesView" && files.params) {
      setShowDialog(true);
      return;
    }

    if (view === "documentsView" && documents.listFromFileLoaded) {
      setShowDialog(true);
      return;
    }

    if (getIsLessThanAMonth()) {
      setShowDialog(true);
    } else {
      setShowTooMuchDialog(true);
    }
  };
  const handleCloseClick = () => {
    setShowDialog(false);
  };

  const handleTooMuchDialogOKClick = () => {
    if (view === "filesView") {
      dispatch(
        actions.change(
          "fileSearch.Start",
          startDate instanceof Date ? startDate : ""
        )
      );
      dispatch(
        actions.change("fileSearch.End", endDate instanceof Date ? endDate : "")
      );
    } else if (view === "documentsView") {
      dispatch(
        actions.change(
          "docSearch.Start",
          startDate instanceof Date ? startDate : ""
        )
      );
      dispatch(
        actions.change("docSearch.End", endDate instanceof Date ? endDate : "")
      );
    }
    setShowTooMuchDialog(false);
    let isLessThanAMonth = false;
    if (startDate instanceof Date && endDate instanceof Date) {
      let lastMonthDate = new Date(endDate);
      lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
      if (lastMonthDate.getTime() <= startDate.getTime()) {
        isLessThanAMonth = true;
      }
    }
    if (isLessThanAMonth) {
      setShowDialog(true);
    }
  };

  const checkAndShowDownload = async (pipelineid, targetAction) => {
    try {
      let params = {
        SearchText: pipelineid,
        PageNumber: 1,
        IsPlatformSpecific: false,
        Size: 50,
      };
      const response = await axios.get("Reporting/reports", { params: params });
      let reCheckAgain = true;
      if (response.data.Errors && response.data.Errors.length > 0) {
        handleErrors(response.data.Errors[0], dispatch, "Download File");
        reCheckAgain = false;
      } else if (
        response.data.ValidationErrors &&
        response.data.ValidationErrors.length > 0
      ) {
        handleErrors(
          response.data.ValidationErrors[0],
          dispatch,
          "Download File"
        );
        reCheckAgain = false;
      } else if (response.data.Data && response.data.Data.length > 0) {
        const downloadReport = response.data.Data[0];
        if (
          downloadReport.reportStatus &&
          downloadReport.reportStatus.st === "finished"
        ) {
          reCheckAgain = false;
          const components = downloadReport.components || [];
          let fullPath = "";
          components.forEach((item) => {
            if (item.type === "exec") {
              if (
                item.additionalInfo &&
                item.additionalInfo.generatedFiles &&
                item.additionalInfo.generatedFiles.length
              ) {
                fullPath = item.additionalInfo.generatedFiles[0];
              }
            }
          });
          if (fullPath) {
            params = {
              PipelineId: pipelineid,
              FileLocation: fullPath,
            };
            dispatch(targetAction.setDownloadFileParams(params));
            dispatch(targetAction.toggleDownLoadSpinner(false));
          } else {
            dispatch(targetAction.toggleDownLoadSpinner(false));
            handleErrors(
              "Server failed to prepare download file",
              dispatch,
              "Download File"
            );
          }
        } else if (
          downloadReport.reportStatus &&
          downloadReport.reportStatus.st === "failed"
        ) {
          reCheckAgain = false;
          dispatch(targetAction.toggleDownLoadSpinner(false));
          handleErrors(
            translate(
              "FileGenerationFailedMsg"
            ),
            dispatch,
            "Download File"
          );
        }
      }
      if (reCheckAgain) {
        setTimeout(() => {
          checkAndShowDownload(pipelineid, targetAction);
        }, INTERVALTIME);
      }
    } catch (error) {
      handleErrors(error, dispatch, "Downloading file");
      dispatch(targetAction.toggleDownLoadSpinner(false));
    }
  };

  const checkAndShowSendingEmail = async (pipelineid, targetAction) => {
    try {
      let params = {
        SearchText: pipelineid,
        PageNumber: 1,
        IsPlatformSpecific: false,
        Size: 50,
      };
      const response = await axios.get("Reporting/reports", { params: params });
      let reCheckAgain = true;
      if (response.data.Errors && response.data.Errors.length > 0) {
        handleErrors(
          response.data.Errors[0],
          dispatch,
          "Sending Download File"
        );
        reCheckAgain = false;
      } else if (
        response.data.ValidationErrors &&
        response.data.ValidationErrors.length > 0
      ) {
        handleErrors(
          response.data.ValidationErrors[0],
          dispatch,
          "Sending Download File"
        );
        reCheckAgain = false;
      } else if (response.data.Data && response.data.Data.length > 0) {
        const downloadReport = response.data.Data[0];
        if (
          downloadReport.reportStatus &&
          downloadReport.reportStatus.st === "finished"
        ) {
          reCheckAgain = false;
          dispatch(targetAction.toggleDownLoadSpinner(false));
          dispatch(targetAction.setEmailSent(true));
        } else if (
          downloadReport.reportStatus &&
          downloadReport.reportStatus.st === "failed"
        ) {
          reCheckAgain = false;
          dispatch(targetAction.toggleDownLoadSpinner(false));
          // email failed but download ready
          const components = downloadReport.components || [];
          let fullPath = "";
          components.forEach((item) => {
            if (item.type === "exec") {
              if (
                item.state &&
                item.state.st === "finished" &&
                item.additionalInfo &&
                item.additionalInfo.generatedFiles &&
                item.additionalInfo.generatedFiles.length
              ) {
                fullPath = item.additionalInfo.generatedFiles[0];
              }
            }
          });
          if (fullPath) {
            params = {
              PipelineId: pipelineid,
              FileLocation: fullPath,
            };
            dispatch(targetAction.setEmailFailedButDownloadReady(params));
          } else {
            handleErrors(
              translate(
                "FileGenerationFailedMsg"
              ),
              dispatch,
              "Sending Download File"
            );
          }
        }
      }
      if (reCheckAgain) {
        setTimeout(() => {
          checkAndShowSendingEmail(pipelineid, targetAction);
        }, INTERVALTIME);
      }
    } catch (error) {
      handleErrors(error, dispatch, "Downloading file");
      dispatch(targetAction.toggleDownLoadSpinner(false));
    }
  };

  const handleDownloadClick = async () => {
    try {
      localStorage.setItem("downloadFileType", fileType);
      localStorage.setItem(
        "downloadIncludeChannelDetails",
        includeChannelDetails ? "true" : ""
      );
      if (view === "filesView") {
        const allColumns = getFileColumns(translate, isExternalUser);
        const fields = allColumns
          .filter((item) => !(files.hiddenColumns || []).includes(item.key))
          .map((item) => item.key);
        let params = getFilesParams(
          {
            ...fileSearch,
            PageNumber: 1,
            Size: 1000,
          },
          isExternalUser,
          externalCustomerId
        );

        if (files.params) {
          params = { ...files.params, ...params };
        }

        dispatch(fileActions.toggleDownLoadSpinner(true));
        const response = await axios.post(
          "Tracking/FileListFromBucketDownload",
          {
            FileType: fileType,
            IncludeChannelDetails: includeChannelDetails,
            Fields: fields,
          },
          { params }
        );
        if (response.data.Errors && response.data.Errors.length > 0) {
          handleErrors(response.data.Errors[0], dispatch, "Download Files");
          dispatch(fileActions.toggleDownLoadSpinner(false));
        } else if (
          response.data.ValidationErrors &&
          response.data.ValidationErrors.length > 0
        ) {
          handleErrors(
            response.data.ValidationErrors[0],
            dispatch,
            "Download Files"
          );
          dispatch(fileActions.toggleDownLoadSpinner(false));
        } else if (response.data.Data && response.data.Data.PipelineId) {
          localStorage.setItem("downloadLoadingForFiles", "true");
          localStorage.setItem("downloadLoadingForDocuments", "");
          localStorage.setItem(
            "downloadPipelineId",
            response.data.Data.PipelineId
          );
          localStorage.setItem("isSendingEmail", "");
          localStorage.setItem("downloadFileUserId", currentUser.userId);
          setTimeout(() => {
            checkAndShowDownload(response.data.Data.PipelineId, fileActions);
          }, INTERVALTIME);
        }
      } else if (view === "documentsView") {
        const allColumns = getDocumentColumns(translate, isExternalUser);
        const fields = allColumns
          .filter((item) => !(documents.hiddenColumns || []).includes(item.key))
          .map((item) => item.key);
        let params = getDocumentsParams(
          {
            ...docSearch,
            PageNumber: 1,
            Size: 1000,
          },
          isExternalUser,
          externalCustomerId,
          isSender
        );
        if (documents.listFromFileLoaded) {
          params = { ...params, ...documents.params };
        }
        dispatch(documentActions.toggleDownLoadSpinner(true));
        const response = await axios.post(
          "Tracking/DocumentListDownload",
          {
            FileType: fileType,
            IncludeChannelDetails: includeChannelDetails,
            Fields: fields,
          },
          { params }
        );
        if (response.data.Errors && response.data.Errors.length > 0) {
          handleErrors(response.data.Errors[0], dispatch, "Download Documents");
          dispatch(documentActions.toggleDownLoadSpinner(false));
        } else if (
          response.data.ValidationErrors &&
          response.data.ValidationErrors.length > 0
        ) {
          handleErrors(
            response.data.ValidationErrors[0],
            dispatch,
            "Download Documents"
          );
          dispatch(documentActions.toggleDownLoadSpinner(false));
        } else if (response.data.Data && response.data.Data.PipelineId) {
          localStorage.setItem("downloadLoadingForFiles", "");
          localStorage.setItem("downloadLoadingForDocuments", "true");
          localStorage.setItem(
            "downloadPipelineId",
            response.data.Data.PipelineId
          );
          localStorage.setItem("isSendingEmail", "");
          localStorage.setItem("downloadFileUserId", currentUser.userId);
          setTimeout(() => {
            checkAndShowDownload(
              response.data.Data.PipelineId,
              documentActions
            );
          }, INTERVALTIME);
        }
      }
      setShowDialog(false);
    } catch (error) {
      dispatch(fileActions.toggleDownLoadSpinner(false));
      dispatch(documentActions.toggleDownLoadSpinner(false));
      handleErrors(error, dispatch, "Download File");
    }
  };

  const handleSendEmailClick = async (event) => {
    event.preventDefault();
    try {
      if (email && validator.isEmail(email)) {
        dispatch(settingActions.changeUserEmail(email, currentUser));
        localStorage.setItem("downloadFileType", fileType);
        localStorage.setItem(
          "downloadIncludeChannelDetails",
          includeChannelDetails ? "true" : ""
        );
        if (view === "filesView") {
          const allColumns = getFileColumns(translate, isExternalUser);
          const fields = allColumns
            .filter((item) => !(files.hiddenColumns || []).includes(item.key))
            .map((item) => item.key);
          let params = getFilesParams(
            { ...fileSearch },
            isExternalUser,
            externalCustomerId
          );
          if (files.params) {
            params = { ...files.params, ...params };
          }
          dispatch(fileActions.toggleDownLoadSpinner(true));
          const response = await axios.post(
            "Tracking/FileListFromBucketDownload",
            {
              FileType: fileType,
              IncludeChannelDetails: includeChannelDetails,
              Fields: fields,
              Email: email,
            },
            { params }
          );
          if (response.data.Errors && response.data.Errors.length > 0) {
            dispatch(fileActions.toggleDownLoadSpinner(false));
            handleErrors(response.data.Errors[0], dispatch, "Sending Email");
          } else if (
            response.data.ValidationErrors &&
            response.data.ValidationErrors.length > 0
          ) {
            dispatch(fileActions.toggleDownLoadSpinner(false));
            handleErrors(
              response.data.ValidationErrors[0],
              dispatch,
              "Sending Email"
            );
          } else if (response.data.Data && response.data.Data.PipelineId) {
            localStorage.setItem("downloadLoadingForFiles", "true");
            localStorage.setItem("downloadLoadingForDocuments", "");
            localStorage.setItem(
              "downloadPipelineId",
              response.data.Data.PipelineId
            );
            localStorage.setItem("isSendingEmail", "true");
            localStorage.setItem("downloadFileUserId", currentUser.userId);
            setTimeout(() => {
              checkAndShowSendingEmail(
                response.data.Data.PipelineId,
                fileActions
              );
            }, INTERVALTIME);
          }
        } else if (view === "documentsView") {
          const allColumns = getDocumentColumns(translate, isExternalUser);
          const fields = allColumns
            .filter(
              (item) => !(documents.hiddenColumns || []).includes(item.key)
            )
            .map((item) => item.key);
          let params = getDocumentsParams(
            { ...docSearch },
            isExternalUser,
            externalCustomerId,
            isSender
          );
          if (documents.listFromFileLoaded) {
            var _additionalParams = {
              FileId: documents.params["FileId"],
            };
            if (documents.params["fidfs"]) {
              _additionalParams["fidfs"] = documents.params["fidfs"];
            }
            if (documents.params["FileChannel"]) {
              _additionalParams["FileChannel"] =
                documents.params["FileChannel"];
            }
            params = { ...params, ..._additionalParams };
          }
          dispatch(documentActions.toggleDownLoadSpinner(true));
          const response = await axios.post(
            "Tracking/DocumentListDownload",
            {
              FileType: fileType,
              IncludeChannelDetails: includeChannelDetails,
              Fields: fields,
              Email: email,
            },
            { params }
          );
          if (response.data.Errors && response.data.Errors.length > 0) {
            handleErrors(response.data.Errors[0], dispatch, "Sending Email");
            dispatch(documentActions.toggleDownLoadSpinner(false));
          } else if (
            response.data.ValidationErrors &&
            response.data.ValidationErrors.length > 0
          ) {
            handleErrors(
              response.data.ValidationErrors[0],
              dispatch,
              "Sending Email"
            );
            dispatch(documentActions.toggleDownLoadSpinner(false));
          } else if (response.data.Data && response.data.Data.PipelineId) {
            localStorage.setItem("downloadLoadingForFiles", "");
            localStorage.setItem("downloadLoadingForDocuments", "true");
            localStorage.setItem(
              "downloadPipelineId",
              response.data.Data.PipelineId
            );
            localStorage.setItem("isSendingEmail", "true");
            localStorage.setItem("downloadFileUserId", currentUser.userId);
            setTimeout(() => {
              checkAndShowSendingEmail(
                response.data.Data.PipelineId,
                documentActions
              );
            }, INTERVALTIME);
          }
        }
        setShowInvalidEmail(false);
        setShowDialog(false);
      } else {
        setShowInvalidEmail(true);
      }
    } catch (error) {
      dispatch(fileActions.toggleDownLoadSpinner(false));
      dispatch(documentActions.toggleDownLoadSpinner(false));
      handleErrors(error, dispatch, "Sending Email");
    }
  };

  const onLastDayBtnClick = () => {
    var _to = moment
        .utc()
        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
        .toDate(),
      _from = moment
        .utc()
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toDate();

    setStartDate(_from);
    setEndDate(_to);
  };

  const onLast7DayBtnClick = () => {
    var _to = moment
        .utc()
        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
        .toDate(),
      _from = moment
        .utc()
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toDate();
    _from.setDate(_from.getDate() - 6);
    setStartDate(_from);
    setEndDate(_to);
  };

  const onLastMonthBtnClick = () => {
    var _to = moment
        .utc()
        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
        .toDate(),
      _from = moment
        .utc()
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toDate();
    _from.setMonth(_from.getMonth() - 1);
    _from.setDate(_from.getDate() + 1);
    setStartDate(_from);
    setEndDate(_to);
  };
  if (
    (!(view === "filesView" && files.items && files.items.length > 0) &&
      !(
        view === "documentsView" &&
        documents.items &&
        documents.items.length > 0
      )) ||
    files.downloadLoading ||
    documents.downloadLoading
  ) {
    return null;
  }

  return (
    <>
      <button
        className="add-issue-btn"
        data-tip
        data-place="right"
        data-type="info"
        data-effect="solid"
        data-for="DownloadBtn"
        onClick={handleClick}
      >
        <i className="fa fa-download" style={{ padding: "0 10px" }} />
      </button>
      <TooltipWithAutoHide
        showInitial
        hideAfter={7000}
        className="customeTheme download-tip"
        id="DownloadBtn"
      >
        <strong>{translate("Download")}</strong>
      </TooltipWithAutoHide>
      <Modal isOpen={showDialog} toggle={handleCloseClick}>
        <ModalHeader toggle={handleCloseClick}>Download</ModalHeader>
        <ModalBody>
          <form>
            <Container>
              <Row form>
                <FormGroup check inline>
                  <legend
                    className="col-form-label"
                    style={{ marginRight: "1em" }}
                  >
                    File type:
                  </legend>
                </FormGroup>
                <Label check className="form-check-inline">
                  <Input
                    type="radio"
                    name="fileType"
                    checked={fileType === "csv"}
                    onChange={(e) => setFileType("csv")}
                  />{" "}
                  CSV
                </Label>
                <Label check className="form-check-inline">
                  <Input
                    type="radio"
                    name="fileType"
                    checked={fileType === "excel"}
                    onChange={(e) => setFileType("excel")}
                  />{" "}
                  Excel
                </Label>
              </Row>
              <Row form>
                <Col md={6} style={{ margin: "0.5em 0" }}>
                  <FormGroup check inline>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={includeChannelDetails}
                        onChange={(e) =>
                          setIncludeChannelDetails(e.target.checked)
                        }
                      />{" "}
                      Include channel details
                    </Label>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <div className="align-items-right">
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ marginRight: "5px" }}
                      onClick={handleDownloadClick}
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ minWidth: "100px" }}
                      onClick={handleCloseClick}
                    >
                      Close
                    </button>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col style={{ margin: "1em 0 0 0" }}>
                  <Alert color="warning">
                    {translate("DownloadRecordsLimitMsg")}
                  </Alert>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Alert color="info">
                    File generation might take time, so instead of waiting you
                    can fill your email address and receive prepared file as
                    soon as its ready
                  </Alert>
                </Col>
              </Row>
              <Row form>
                <Col md={8}>
                  <FormGroup row>
                    <Label for="downloadEmail" sm={2}>
                      Email
                    </Label>
                    <Col sm={10}>
                      <Input
                        type="email"
                        name="email"
                        id="downloadEmail"
                        value={email}
                        invalid={!!email && !validator.isEmail(email)}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Col>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <button
                    disabled={!validator.isEmail(email)}
                    type="submit"
                    className="btn btn-primary"
                    style={{ padding: "0.85rem 1rem", width: "100%" }}
                    onClick={handleSendEmailClick}
                  >
                    Send to Email
                  </button>
                </Col>
              </Row>
              {showInvalidEmail && (
                <Row>
                  <Col style={{ margin: "0.5em 0 0 0" }}>
                    <Alert color="danger">
                      Please enter valid email address
                    </Alert>
                  </Col>
                </Row>
              )}
            </Container>
          </form>
        </ModalBody>
      </Modal>
      <Modal
          className="download-modal"
          isOpen={ !!showTooMuchDialog }
          toggle={ () => setShowTooMuchDialog(false) }
      >
        <ModalHeader toggle={ () => setShowTooMuchDialog(false) }>
          Download
        </ModalHeader>
        <ModalBody>
          <Alert color="warning" style={{ margin: "0 0 1.5em 0" }}>
            {translate("tooManyItemsToDownload", {
              items: translate(view === "filesView" ? "files" : "documents"),
            })}
          </Alert>
          <div
            className="container time-period-btns"
            style={{ width: "fit-content" }}
          >
            <Row>
              <Col style={{ padding: 0 }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={onLastDayBtnClick}
                >
                  {translate("LastDay")}
                </button>
              </Col>
              <Col style={{ padding: 0 }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={onLast7DayBtnClick}
                >
                  {translate("Last 7 days")}
                </button>
              </Col>
              <Col style={{ padding: 0 }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={onLastMonthBtnClick}
                >
                  {translate("Last Month")}
                </button>
              </Col>
            </Row>
          </div>
          <Row form>
            <Col className="input-container" md={ 6 } style={ { margin: '1em 0 0 0' } }>
              <FormGroup row>
                <Label>
                  { translate('From') }
                </Label>
                <Datetime
                    value={ startDate }
                    utc={ true }
                    locale={ getMomentLocaleByLang(currentUser?.language) }
                    dateFormat="DD-MM-YYYY"
                    timeFormat="HH:mm"
                    inputProps={ {
                      placeholder: 'dd-MM-yyyy HH:mm',
                      className: 'form-control'
                    } }
                    onChange={ (Start) => {
                      const _date = moment(Start).toDate();
                      setStartDate(_date instanceof Date ? _date : '');
                    } }
                />
              </FormGroup>
            </Col>
            <Col className="input-container" md={ 6 } style={ { margin: '1em 0 0 0' } }>
              <FormGroup row>
                <Label>{ translate('Till') }</Label>
                <Datetime
                    value={ endDate }
                    utc={ true }
                    locale={ getMomentLocaleByLang(currentUser?.language) }
                    dateFormat="DD-MM-YYYY"
                    timeFormat="HH:mm"
                    inputProps={ {
                      placeholder: 'dd-MM-yyyy HH:mm',
                      className: 'form-control'
                    } }
                    onChange={ (End) => {
                      const _date = moment(End).toDate();
                      setEndDate(_date instanceof Date ? _date : '');
                    } }
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="text-center">
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "100px" }}
            onClick={ handleTooMuchDialogOKClick }
            disabled={ !getIsLessThanAMonth(startDate, endDate) }
          >
            OK
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

function mapStateToProps(state) {
  const { files, documents, fileSearch, docSearch, locale, authentication } =
    state;
  const { user, subUser, isSubUser } = authentication;
  return {
    user,
    subUser,
    isSubUser,
    files,
    documents,
    fileSearch,
    docSearch,
    translate: getTranslate(locale),
  };
}

const connectedQuickDownloadButton =
  connect(mapStateToProps)(QuickDownloadButton);

export { connectedQuickDownloadButton as QuickDownloadButton };
